import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Clock } from "lucide-react";

import { blogPosts } from "@/data/blog";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/blog";
const title = "Car Rental Guides Bucharest — No Deposit Tips";
const description =
  "Guides on renting a car in Bucharest and Romania with no deposit and no credit card: real prices, airport pickup at Otopeni, documents and insurance explained.";

export const Route = createFileRoute("/blog/")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": `${CANONICAL}#blog`,
          name: "Car Rental Bucharest Guides",
          url: CANONICAL,
          publisher: { "@id": "https://carentals.ro/#organization" },
          blogPost: blogPosts.map((post) => ({
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            datePublished: post.datePublished,
            url: `https://carentals.ro/blog/${post.slug}`,
          })),
        }),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="container-page py-16">
      <p className="eyebrow">Guides</p>
      <h1 className="mt-3 max-w-3xl text-4xl font-extrabold sm:text-5xl">
        Renting a car in Bucharest — no deposit, no credit card
      </h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">
        Practical guides on car rental in Romania: how deposits work, how to rent with a debit card
        or cash, what airport pickup at Otopeni really costs, and what is included in our prices.
      </p>
      <p className="mt-4 max-w-3xl text-muted-foreground">
        Everything here is written from what we do every day in Bucharest — handing over cars at our
        office on Bulevardul Regina Elisabeta 33 and at Otopeni Airport, with no security deposit and
        no credit card required. The guides cover the paperwork you actually need, how insurance and
        the rovinieta road tax are already included in the daily price, and what to expect on
        Romanian roads. Start with{" "}
        <Link to="/blog/$slug" params={{ slug: "car-rental-no-deposit-romania" }} className="text-gold hover:underline">
          no-deposit rental in Romania
        </Link>
        ,{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "rent-a-car-without-credit-card-bucharest" }}
          className="text-gold hover:underline"
        >
          renting without a credit card
        </Link>{" "}
        or{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "cheap-car-rental-bucharest-otopeni-airport" }}
          className="text-gold hover:underline"
        >
          cheap rental at Otopeni Airport
        </Link>
        . For on-the-ground practicalities see{" "}
        <Link to="/good-to-know" className="text-gold hover:underline">
          Good to know in Bucharest
        </Link>
        .
      </p>

      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {blogPosts.map((post) => (
          <article
            key={post.slug}
            className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-card"
          >
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-gold">
              <Clock className="size-3.5" /> {post.readingMinutes} min read
            </p>
            <h2 className="mt-3 font-display text-xl font-bold leading-snug">
              <Link to="/blog/$slug" params={{ slug: post.slug }} className="hover:text-gold">
                {post.title}
              </Link>
            </h2>
            <p className="mt-3 flex-1 text-sm text-muted-foreground">{post.excerpt}</p>
            <Link
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold"
            >
              Read the guide <ArrowRight className="size-4" />
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}
